import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { IconUserHeart, IconUserMinus } from '@tabler/icons-react';
import { cn } from '../../../lib/utils';
import sessionEngagementActivityAnalyticService from '../../../services/session-engagement-activity-analytics.service';

const SessionStats = () => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setError(false);
      setLoading(true);
      try {
        const data =
          await sessionEngagementActivityAnalyticService.getSessionDetails();
        setStatsData(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
        setError('Failed to load statistics.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (error) {
    return (
      <div className="col-span-full">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  const averageSessionDuration = statsData?.average_session_duration_minutes;
  const averageSessionPerUser = statsData?.average_sessions_per_user;
  const inactiveUsers = statsData?.users_with_0_sessions;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
      <Card className="shadow-sm border-0 h-full">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-3">
            <div
              className={cn(
                'p-2 rounded',
                loading ? 'bg-gray-100' : 'bg-blue-500 text-white'
              )}
              style={{ width: 40, height: 40 }}
            >
              <IconUserHeart size={24} />
            </div>

            {loading && <Skeleton className="w-[60px] h-5" />}
          </div>

          <div>
            <h5 className="text-lg font-bold mb-2">Average Session Overview</h5>

            <div className="flex justify-between">
              <div>
                <p className="text-muted-foreground text-sm mb-1">Average Session Duration</p>
                <h5 className="text-xl font-semibold">
                  {loading ? (
                    <Skeleton className="w-[60px] h-6" />
                  ) : (
                    `${averageSessionDuration} min`
                  )}
                </h5>
              </div>

              <div>
                <p className="text-muted-foreground text-sm mb-1">
                  Average Session per User (Weekly)
                </p>
                <h5 className="text-xl font-semibold">
                  {loading ? (
                    <Skeleton className="w-[60px] h-6" />
                  ) : (
                    `${averageSessionPerUser}`
                  )}
                </h5>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Card className="shadow-sm border-0 h-full">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-3">
            <div
              className={cn(
                'p-2 rounded',
                loading ? 'bg-gray-100' : 'bg-purple-500 text-white'
              )}
              style={{ width: 40, height: 40 }}
            >
              <IconUserMinus size={24} />
            </div>

            {loading && <Skeleton className="w-[60px] h-5" />}
          </div>

          <div>
            <h5 className="text-lg font-bold mb-2">User Inactivity Overview</h5>

            <div className="flex justify-between">
              <div>
                <p className="text-muted-foreground text-sm mb-1">No Activity in Past 7 Days</p>
                <h5 className="text-xl font-semibold">
                  {loading ? (
                    <Skeleton className="w-[60px] h-6" />
                  ) : (
                    `${inactiveUsers?.last_7_days.length}`
                  )}
                </h5>
              </div>

              <div>
                <p className="text-muted-foreground text-sm mb-1">No Activity in Past 14 Days</p>
                <h5 className="text-xl font-semibold">
                  {loading ? (
                    <Skeleton className="w-[60px] h-6" />
                  ) : (
                    `${inactiveUsers?.last_14_days.length}`
                  )}
                </h5>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionStats;
